"use client";

import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const membershipData = [
  { year: "2020", members: 18 },
  { year: "2021", members: 34 },
  { year: "2022", members: 58 },
  { year: "2023", members: 87 },
  { year: "2024", members: 124 },
  { year: "2025", members: 163 },
  { year: "2026", members: 210 },
];

function GrowthCurve({ isInView }) {
  const W = 800;
  const H = 160;
  const padX = 50;
  const padY = 24;

  const maxVal = Math.max(...membershipData.map((d) => d.members));
  const pts = membershipData.map((d, i) => ({
    x: padX + (i / (membershipData.length - 1)) * (W - padX * 2),
    y: H - padY - (d.members / maxVal) * (H - padY * 2),
    ...d,
  }));

  const path = pts.reduce((acc, p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = pts[i - 1];
    const cpx = (prev.x + p.x) / 2;
    return `${acc} C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
  }, "");

  const area = `${path} L ${pts[pts.length - 1].x} ${H} L ${pts[0].x} ${H} Z`;

  return (
    <div className="w-full max-w-3xl mx-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="areaGrad2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#7dd3fc" stopOpacity="0" />
          </linearGradient>
          <clipPath id="revealClip2">
            <motion.rect
              x="0" y="0" height={H}
              initial={{ width: 0 }}
              animate={isInView ? { width: W } : { width: 0 }}
              transition={{ duration: 1.8, ease: "easeOut", delay: 0.3 }}
            />
          </clipPath>
        </defs>

        {/* Subtle grid lines */}
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line
            key={f}
            x1={padX} x2={W - padX}
            y1={H - padY - f * (H - padY * 2)}
            y2={H - padY - f * (H - padY * 2)}
            stroke="rgba(255,255,255,0.07)" strokeWidth="1" strokeDasharray="4 4"
          />
        ))}

        {/* Area fill */}
        <path d={area} fill="url(#areaGrad2)" clipPath="url(#revealClip2)" />

        {/* Curve line */}
        <path
          d={path}
          fill="none"
          stroke="#7dd3fc"
          strokeWidth="2.5"
          strokeLinecap="round"
          clipPath="url(#revealClip2)"
        />

        {/* Dots + labels */}
        {pts.map((p, i) => (
          <motion.g key={i}
            initial={{ opacity: 0, scale: 0 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0 }}
            transition={{ delay: 0.3 + i * 0.18, duration: 0.3 }}
          >
            <circle cx={p.x} cy={p.y} r="5" fill="#7dd3fc" stroke="rgba(0,34,51,0.9)" strokeWidth="2" />
            {/* Member count above dot */}
            <text x={p.x} y={p.y - 12} textAnchor="middle" fill="white" fontSize="11" fontWeight="700" fontFamily="inherit">
              {p.members}
            </text>
            {/* Year below chart */}
            <text x={p.x} y={H - 3} textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="10" fontFamily="inherit">
              {p.year}
            </text>
          </motion.g>
        ))}
      </svg>
    </div>
  );
}

export default function AboutTeamDivider() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });

  return (
    <section ref={ref} className="relative py-10 md:py-14 overflow-hidden flex items-center justify-center">
      {/* BACKGROUND */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "url('/bg.webp')",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundAttachment: "fixed",
        }}
      />

      {/* BLUE OVERLAY */}
      <div
        className="absolute inset-0 z-0"
        style={{
          background: "linear-gradient(135deg, rgba(0,102,153,0.88) 0%, rgba(0,34,51,0.95) 100%)",
        }}
      />

      {/* CONTENT */}
      <div className="relative z-10 w-full max-w-3xl mx-auto px-6 sm:px-10 flex flex-col items-center gap-4">

        {/* Label */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-white text-2xl sm:text-3xl tracking-wide font-extralight"
        >
          IEEE ISIMS SB —{" "}
          <span className="font-serif italic font-medium text-[#7dd3fc]">Membership Growth</span>
        </motion.p>

        {/* Chart */}
        <GrowthCurve isInView={isInView} />

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 1.5, duration: 0.6 }}
          className="text-white/30 text-[10px] tracking-widest uppercase"
        >
          Members across all years
        </motion.p>
      </div>
    </section>
  );
}
